import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

import type { AnimatedIdsRef } from './chat-animated-item.atom'
import type { ChatInteractionHandlers } from './chat-interaction-handlers.interface'
import type { ChatItem } from './chat-message.interface'
import { ChatScrollToBottomButton } from './chat-scroll-to-bottom-button.atom'
import { ChatVirtuosoWrapper } from './chat-virtuoso-wrapper.atom'
import type { UseChatVirtualListReturn } from './use-chat-virtual-list.hook'
import { useChatVirtualList } from './use-chat-virtual-list.hook'

export interface ChatVirtualListProps extends Omit<ChatInteractionHandlers, 'pinnedItemIdList'> {
  itemList: ChatItem[]
  pinnedItemIdList?: string[]
  isFollowOutputEnabled?: boolean
  isAnimationEnabled?: boolean
  conversationId?: string
  onLoadMore?: () => void
  hasMoreMessages?: boolean
  isFetchingMoreMessages?: boolean
  /**
   * Optional external virtual-list binding. When provided, the component uses
   * it instead of creating its own via useChatVirtualList. Lets callers share
   * the virtuosoRef + firstItemIndex with other hooks (e.g. scroll-to-pinned).
   */
  virtualListBinding?: UseChatVirtualListReturn
  className?: string
}

export const ChatVirtualList: React.FC<ChatVirtualListProps> = ({
  itemList,
  currentUserId,
  pinnedItemIdList = [],
  highlightedMessageId = null,
  isFollowOutputEnabled = true,
  isAnimationEnabled = true,
  conversationId,
  onLoadMore,
  hasMoreMessages = false,
  isFetchingMoreMessages = false,
  virtualListBinding,
  className,
  ...interactionHandlers
}): ReactNode => {
  const fallbackBinding = useChatVirtualList({
    itemList,
    conversationId,
    isFollowOutputEnabled,
    isFetchingMoreMessages,
    hasMoreMessages,
    onLoadMore,
  })
  const binding = virtualListBinding ?? fallbackBinding

  return (
    <div className={cn('chat-messages-list relative h-full w-full overflow-hidden', className)}>
      <ChatVirtuosoWrapper
        virtuosoRef={binding.virtuosoRef}
        itemList={itemList}
        renderItemConfig={{
          currentUserId,
          pinnedItemIdList,
          highlightedMessageId,
          isAnimationEnabled,
          animatedIdsRef: binding.animatedIdsRef as AnimatedIdsRef,
          ...interactionHandlers,
        }}
        handleAtTopStateChange={binding.handleAtTopStateChange}
        handleAtBottomStateChange={binding.handleAtBottomStateChange}
        handleFollowOutput={binding.handleFollowOutput}
        firstItemIndex={binding.firstItemIndex}
        isFetchingMoreMessages={isFetchingMoreMessages}
      />
      <ChatScrollToBottomButton
        isVisible={binding.showScrollButton}
        onClick={binding.scrollToBottom}
      />
    </div>
  )
}

ChatVirtualList.displayName = 'ChatVirtualList'
